import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import {
  CheckCircle2,
  Sparkles,
  PhoneCall,
  ShieldCheck,
  Cpu,
  Wrench,
  Box,
  CreditCard,
  Activity,
  Building2,
  Factory,
  Dumbbell,
  Hospital,
  GraduationCap,
  Phone,
  Truck,
  Hotel,
  Store,
} from "lucide-react";
import SiteVisitForm from "@/components/SiteVisitForm";
import VideoBackground from "@/components/VideoBackground";
import StatsStrip from "@/components/StatsStrip";

const HERO_IMG =
  "/media/vending-machine-orange-wall.png";

export default function Home() {
  return (
    <div data-testid="home-page" className="text-white">
      <Seo />
      <HeroSection />
      <StatsStrip />
      <BuyVsFreeSection />
      <WhatYouGetSection />
      <WhyChooseSection />
      <PerfectForSection />
      <HowItWorksSection />
      <FormSection />
    </div>
  );
}

function HeroSection() {
  return (
    <section className="relative overflow-hidden border-b border-white/5">
      <VideoBackground testId="home-hero-video" />
      <div className="relative mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#FE7200]/30 bg-[#FE7200]/10 px-3 py-1 text-xs font-semibold tracking-wide text-[#FE7200]">
            <Sparkles className="h-3.5 w-3.5" /> Anytime Vending Machines
          </div>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-balance">
            <span className="text-gradient">Free Vending Machines</span> for Qualified Sites with Anytime Vending Machines
          </h1>
          <p className="mt-5 text-base sm:text-lg text-zinc-300 max-w-2xl leading-relaxed">
            Fully stocked. Fully serviced. <span className="text-white font-semibold">Zero cost</span> for qualifying sites. Installed at your workplace within days <span className="text-white font-semibold">(30+ staff</span> or steady foot traffic).
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <a
              href="#book-form"
              data-testid="hero-book-button"
              className="btn-shine neon-glow neon-glow-hover inline-flex items-center justify-center rounded-full bg-[#FE7200] px-6 py-3 text-sm font-bold uppercase tracking-wider text-black hover:bg-[#FF8C2E] hover:scale-[1.03] transition-all duration-300"
            >
              Book a Free Site Visit
            </a>
            <a
              href="#how-it-works"
              data-testid="hero-how-button"
              className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/5 backdrop-blur-sm px-6 py-3 text-sm font-bold uppercase tracking-wider text-white hover:bg-white/10 hover:border-[#FE7200]/40 transition-all duration-300"
            >
              How It Works
            </a>
            <a
              href="#book-form"
              data-testid="hero-contact-button"
              className="inline-flex items-center justify-center rounded-full border border-[#FE7200]/50 bg-[#FE7200]/15 px-6 py-3 text-sm font-bold uppercase tracking-wider text-[#FF8C2E] hover:bg-[#FE7200]/25 transition-colors"
            >
              Contact Us
            </a>
          </div>

          <ul className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-300">
            {[
              "Brand new machines",
              "Tap & Go + cash/coin",
              "No lock-in contracts",
              "4-week trial option",
            ].map((b) => (
              <li key={b} className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#FE7200]" /> {b}
              </li>
            ))}
          </ul>

          <div className="mt-10 relative rounded-2xl overflow-hidden border border-white/10 bg-[#121212]">
            <img
              src={HERO_IMG}
              alt="Modern vending machine"
              className="w-full h-72 sm:h-96 object-cover opacity-95"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 text-sm sm:text-base">
              <span className="font-semibold text-white">Modern, professional machines</span>
              <span className="text-zinc-300"> with tap & go — stocked, serviced and monitored by us.</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div
            data-testid="hero-booking-card"
            id="hero-book-form"
            className="rounded-2xl border border-white/10 bg-[#121212] p-6 sm:p-7 shadow-[0_30px_60px_-20px_rgba(254,114,0,0.25)]"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FE7200]">
                  Book a Free Site Visit
                </div>
                <h3 className="mt-1 font-display text-xl font-bold text-white">
                  Zero-cost install for qualifying sites.
                </h3>
                <p className="mt-1.5 text-sm text-zinc-400">
                  Fill this out and we'll confirm eligibility and reach out within one business day.
                </p>
              </div>
              <ShieldCheck className="h-7 w-7 shrink-0 text-[#FE7200]" />
            </div>
            <SiteVisitForm source="home-hero" testIdPrefix="hero-booking" />
          </div>
        </div>
      </div>

      {/* scroll indicator */}
      <a
        href="#looking-to-buy"
        data-testid="hero-scroll-indicator"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-1.5 text-zinc-400 hover:text-[#FE7200] transition-colors"
        aria-label="Scroll to next section"
      >
        <span className="text-[10px] font-bold uppercase tracking-[0.25em]">Scroll</span>
        <span className="animate-scroll-nudge inline-flex items-center justify-center h-9 w-9 rounded-full border border-white/15 bg-black/40 backdrop-blur-sm">
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 5v14M19 12l-7 7-7-7" />
          </svg>
        </span>
      </a>
    </section>
  );
}

function BuyVsFreeSection() {
  return (
    <section id="looking-to-buy" className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-20 md:py-28" data-testid="buy-vs-free-section">
      <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
        Looking to buy a vending machine?
      </h2>
      <p className="mt-3 text-zinc-300 max-w-3xl">
        Most businesses don't need to buy one at all. We supply modern vending machines completely free to workplaces, gyms, warehouses, offices and facilities with{" "}
        <span className="text-white font-semibold">30+ staff</span> or steady foot traffic.
      </p>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card title="What you pay" testId="card-what-you-pay">
          <List
            items={[
              <><b className="text-white">No rental.</b> No weekly fees.</>,
              <><b className="text-white">No servicing fees.</b> We maintain it.</>,
              <><b className="text-white">No contracts.</b> If it doesn't work, we remove it.</>,
            ]}
          />
        </Card>
        <Card title="What you get" testId="card-what-you-get">
          <List
            items={[
              <><b className="text-white">You get the machine.</b> We do the rest.</>,
              <><b className="text-white">Fast install.</b> Usually within days after site visit.</>,
              <><b className="text-white">Custom stock.</b> Snacks, drinks, meals, healthy options.</>,
            ]}
          />
        </Card>
      </div>

      <div className="mt-8 rounded-2xl border border-[#FE7200]/30 bg-gradient-to-r from-[#FE7200]/15 to-transparent p-6 md:p-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <div className="font-display text-xl md:text-2xl font-bold text-white">
            Free installation for workplaces with 30+ staff
          </div>
          <div className="text-sm text-zinc-300 mt-1">
            Below 30? Still ask — foot traffic and site type can qualify too.
          </div>
        </div>
        <a
          href="#book-form"
          data-testid="buy-vs-free-cta-button"
          className="inline-flex items-center justify-center rounded-full bg-[#FE7200] px-6 py-3 text-sm font-bold uppercase tracking-wider text-black hover:bg-[#FF8C2E] transition-colors"
        >
          Book Free Site Visit
        </a>
      </div>
    </section>
  );
}

function WhatYouGetSection() {
  const items = [
    { icon: Box, title: "Brand new modern machine", text: "Clean, professional looking units that suit modern workplaces." },
    { icon: CreditCard, title: "Tap & Go + cash/coin", text: "PayWave, cards, phones, smart watches — plus cash and coins." },
    { icon: Sparkles, title: "Full product range", text: "Drinks, snacks, meals and healthier options — you choose the mix." },
    { icon: Wrench, title: "Stocked & serviced by us", text: "Regular restocking and maintenance — always clean and reliable." },
    { icon: Activity, title: "Back-to-base monitoring", text: "We track stock levels so refills happen before the machine runs out." },
    { icon: ShieldCheck, title: "Safety tested", text: "Electrical safety tested and ready to go after installation." },
  ];
  return (
    <section className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-20 md:py-28 border-t border-white/5" data-testid="what-you-get-section">
      <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">What you get</h2>
      <p className="mt-3 text-zinc-300 max-w-3xl">
        A professional vending setup that actually gets used — and stays stocked.
      </p>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((it, idx) => (
          <FeatureCard key={idx} {...it} testId={`feature-card-${idx}`} />
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card title="Risk-free trial" testId="card-risk-free">
          <List
            items={[
              <><b className="text-white">4 week</b> no obligation trial option</>,
              <>No lock-in contract</>,
              <>If it's not being used, we remove it</>,
            ]}
          />
        </Card>
        <Card title="Machine specifications" testId="card-specs">
          <List
            items={[
              <><b className="text-white">Height:</b> 1830mm</>,
              <><b className="text-white">Width:</b> 1050mm</>,
              <><b className="text-white">Depth:</b> 830mm</>,
              <><b className="text-white">Power usage:</b> Approx. $100/year</>,
              <><b className="text-white">Standard power point</b> required</>,
            ]}
          />
        </Card>
      </div>
    </section>
  );
}

function FeatureCard({ icon: Icon, title, text, testId }) {
  return (
    <div
      data-testid={testId}
      className="group rounded-2xl border border-white/10 bg-[#121212] p-6 hover:border-[#FE7200]/40 hover:-translate-y-1 transition-all"
    >
      <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#FE7200]/15 border border-[#FE7200]/30 text-[#FE7200]">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="mt-4 font-display text-lg font-bold text-white">{title}</h3>
      <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{text}</p>
    </div>
  );
}

function Card({ title, children, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-white/10 bg-[#121212] p-6 md:p-7">
      <h3 className="font-display text-xl font-bold text-white">{title}</h3>
      <div className="mt-4 text-sm">{children}</div>
    </div>
  );
}

function List({ items }) {
  return (
    <ul className="space-y-2 text-zinc-300">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-2">
          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#FE7200]" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function WhyChooseSection() {
  return (
    <section className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-20 md:py-28 border-t border-white/5" data-testid="why-choose-section">
      <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
        Why businesses choose Anytime Vending Machines
      </h2>
      <p className="mt-3 text-zinc-300 max-w-3xl">
        Most vending machines are old, poorly stocked, dirty, cash-only and rarely serviced. We built Anytime Vending Machines to fix that.
      </p>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-white/10 bg-[#121212] p-6 md:p-7" data-testid="card-typical-vending">
          <span className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-300">
            Typical Vending
          </span>
          <List
            items={[
              "Old machines",
              "Poor stock consistency",
              "Dirty / neglected",
              "Cash only",
              "Rarely serviced",
            ]}
          />
        </div>
        <div className="rounded-2xl border border-[#FE7200]/40 bg-[#FE7200]/5 p-6 md:p-7" data-testid="card-anytime-vending">
          <span className="inline-flex items-center rounded-full bg-[#FE7200] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-black">
            Anytime Vending
          </span>
          <List
            items={[
              "Professional-looking machines",
              "Modern payments accepted",
              "Automatic restocking signals",
              "Cleaned and maintained",
              "Staff actually use them",
            ]}
          />
        </div>
      </div>
    </section>
  );
}

function PerfectForSection() {
  const tags = [
    { label: "Warehouses", icon: Building2 },
    { label: "Factories", icon: Factory },
    { label: "Gyms & fitness centers", icon: Dumbbell },
    { label: "Hospitals & clinics", icon: Hospital },
    { label: "Schools & universities", icon: GraduationCap },
    { label: "Call centers", icon: Phone },
    { label: "Workshops", icon: Wrench },
    { label: "Distribution centers", icon: Truck },
    { label: "Offices", icon: Building2 },
    { label: "Hotels", icon: Hotel },
    { label: "Transport depots", icon: Truck },
    { label: "Large retail stores", icon: Store },
  ];
  return (
    <section className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-20 md:py-28 border-t border-white/5" data-testid="perfect-for-section">
      <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Perfect for</h2>
      <p className="mt-3 text-zinc-300">We install machines across the country in workplaces like:</p>
      <div className="mt-6 flex flex-wrap gap-3">
        {tags.map((t, i) => (
          <span
            key={t.label}
            data-testid={`perfect-for-tag-${i}`}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#121212] px-4 py-2 text-sm text-zinc-200 hover:border-[#FE7200]/50 transition-colors"
          >
            <t.icon className="h-4 w-4 text-[#FE7200]" /> {t.label}
          </span>
        ))}
      </div>

      <div className="mt-8 rounded-2xl border border-white/10 bg-gradient-to-r from-[#FE7200]/15 to-[#FE7200]/10 p-6 md:p-7 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="font-display text-xl md:text-2xl font-bold text-white">
            If you have 30+ staff or regular foot traffic, you qualify
          </div>
          <p className="text-sm text-zinc-300 mt-1">
            Book a free site visit and we'll confirm in minutes.
          </p>
        </div>
        <a
          href="#book-form"
          data-testid="perfect-for-cta"
          className="inline-flex items-center justify-center rounded-full bg-[#FE7200] px-6 py-3 text-sm font-bold uppercase tracking-wider text-black hover:bg-[#FF8C2E] transition-colors"
        >
          Book Free Site Visit
        </a>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const steps = [
    { n: 1, title: "Free site visit", text: "We check space and access, confirm staff numbers, and recommend the best setup." },
    { n: 2, title: "Choose your products", text: "Pick soft drinks, water, energy drinks, snacks, healthy options, kombucha and more." },
    { n: 3, title: "We install it", text: "We deliver, test and install the machine. It's ready to use immediately." },
    { n: 4, title: "We keep it full", text: "Stock levels are monitored automatically. When levels drop, we come refill it fast." },
  ];
  return (
    <section id="how-it-works" className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-20 md:py-28 border-t border-white/5" data-testid="how-it-works-section">
      <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">How it works</h2>
      <p className="mt-3 text-zinc-300">Simple process. Zero hassle. We handle everything end-to-end.</p>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
        {steps.map((s, idx) => (
          <div
            key={s.n}
            data-testid={`how-it-works-step-${idx}`}
            className="rounded-2xl border border-white/10 bg-[#121212] p-6 md:p-7 flex gap-5"
          >
            <div className="shrink-0 h-10 w-10 rounded-full bg-[#FE7200] text-black font-display font-extrabold flex items-center justify-center text-lg">
              {s.n}
            </div>
            <div>
              <h3 className="font-display text-lg font-bold text-white">{s.title}</h3>
              <p className="text-sm text-zinc-400 mt-1">{s.text}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function FormSection() {
  return (
    <section id="book-form" className="mx-auto max-w-5xl px-4 md:px-8 lg:px-12 py-20 md:py-28 border-t border-white/5" data-testid="home-form-section">
      <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Book a Free Site Visit</h2>
      <p className="mt-3 text-zinc-300">
        Fill this out and we'll confirm eligibility and contact you to arrange the best time.
      </p>
      <div className="mt-8">
        <SiteVisitForm source="home-booking" testIdPrefix="home-booking" />
      </div>
    </section>
  );
}
